import * as React from "react";
import type { BaseStylableProps } from "../../../types/ui";
import { DataGridPro } from "@mui/x-data-grid-pro";
import {
  type DataGridProProps,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GridFooterContainer,
  type GridColDef,
  type GridColumnHeaderParams,
  type GridApiPro,
  type GridRenderCellParams,
  type GridSortDirection,
  type GridSortModel,
  type GridRowId,
  type GridRowParams,
  type GridAutosizeOptions,
  useGridApiRef,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
  gridPageCountSelector,
  gridPaginationMetaSelector,
  gridPaginationModelSelector,
  gridPaginationRowCountSelector,
  gridVisibleRowsSelector,
} from "@mui/x-data-grid-pro";
import styles from "./DataTable.module.scss";
import clsx from "clsx";
import Button from "../Button/Button";
import Icon from "../Icon/Icon";
import Select from "../../form/Select/Select";
import {
  DataTableBaseIconButton,
  DataTableBaseDivider,
  DataTableBaseMenuItem,
  DataTableBaseMenuList,
  DataTableColumnMenuIcon,
  DataTableFilterIcon,
  DataTableHideColumnIcon,
  DataTableManageColumnsIcon,
  DataTableSortAscendingIcon,
  DataTableSortDescendingIcon,
} from "./DataTableMenu";
import {
  DataTableBaseButton,
  DataTableBaseCheckbox,
  DataTableBaseSelect,
  DataTableBaseSelectOption,
  DataTableBaseTextField,
} from "./DataTablePanelControls";
import { withRefProp } from "../../../utils/withRefProp";

export type DataTableColumn = GridColDef & {
  autoSize?: boolean;
};
export type DataTableSortModel = GridSortModel;
export type DataTableSortDirection = GridSortDirection;
export type DataTableColumnHeaderParams = GridColumnHeaderParams;
export type DataTableRowParams = GridRowParams;
export type DataTableHandle = {
  scrollToIndexes: GridApiPro["scrollToIndexes"];
};
export const useDataTableHandleRef = () => React.useRef<DataTableHandle | null>(null);
export type DataTableProps = BaseStylableProps & Omit<DataGridProProps, "autosizeOptions" | "autosizeOnMount"> & {
  ref?: React.Ref<HTMLDivElement>;
  autosizeKey?: string | number;
  handleRef?: React.Ref<DataTableHandle>;
  /** The row to show as selected, for example the row whose details are open beside the table. */
  selectedRowId?: GridRowId | null;
};

const DataTableFooter = ({ rowCountKnown }: { rowCountKnown: boolean }) => {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
  const rowCount = useGridSelector(apiRef, gridPaginationRowCountSelector);
  const paginationMeta = useGridSelector(apiRef, gridPaginationMetaSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const visibleRows = useGridSelector(apiRef, gridVisibleRowsSelector);

  const { page, pageSize } = paginationModel;

  const pageSizeOptions = React.useMemo(() => {
    const options = rootProps.pageSizeOptions.map((option) => {
        if (typeof option === "number") {
          return { value: String(option), label: String(option) };
        }

        return { value: String(option.value), label: option.label };
      });
    const pageSizeValue = String(pageSize);

    if (!options.some((option) => option.value === pageSizeValue)) {
      return [{ value: pageSizeValue, label: pageSizeValue }, ...options];
    }

    return options;
  }, [rootProps.pageSizeOptions, pageSize]);

  if (!rootProps.pagination) return null;

  const isUnknownRowCount = !rowCountKnown || rowCount < 0;
  const unknownVisibleRowCount = visibleRows.rows.length;
  const isLoading = Boolean(rootProps.loading);
  const hasRows = isUnknownRowCount ? (unknownVisibleRowCount > 0 || isLoading) : rowCount !== 0;
  const firstRow = hasRows ? page * pageSize + 1 : 0;
  const lastKnownRow = hasRows && !isUnknownRowCount ? Math.min(rowCount, (page + 1) * pageSize) : 0;
  const assumedUnknownLastRow = page * pageSize + pageSize;
  const lastDisplayedRow = isUnknownRowCount && hasRows
    ? (unknownVisibleRowCount > 0 ? page * pageSize + unknownVisibleRowCount : assumedUnknownLastRow)
    : lastKnownRow;
  const rowCountLabel = isUnknownRowCount ? "many" : rowCount.toLocaleString();
  const paginationSummary = !hasRows
    ? "No rows"
    : isUnknownRowCount
      ? `${firstRow.toLocaleString()}-${lastDisplayedRow.toLocaleString()}`
      : `${firstRow.toLocaleString()}-${lastDisplayedRow.toLocaleString()} of ${rowCountLabel}`;
  const hasNextPage = isUnknownRowCount
    ? paginationMeta.hasNextPage !== false
    : pageCount > 0 && page < pageCount - 1;

  return (
    <GridFooterContainer className={styles.footer}>
      <div className={styles.pageSizeControl}>
        <span className={styles.footerLabel}>Rows per page</span>
        <Select
          className={styles.pageSizeSelect}
          aria-label="Rows per page"
          searchable={false}
          menuPlacement="top"
          options={pageSizeOptions}
          value={String(pageSize)}
          onValueChange={(value) => {
            if (!value) return;
            apiRef.current.setPaginationModel({ page: 0, pageSize: Number(value) });
          }}
        />
      </div>
      <div className={styles.paginationSummary} data-deci-data-table-pagination-summary="true">
        {paginationSummary}
      </div>
      <div className={styles.paginationActions}>
        <Button
          icon
          size="sm"
          variant="ghost"
          label="First page"
          disabled={page === 0}
          onClick={() => apiRef.current.setPage(0)}
        >
          <Icon name="go-first" />
        </Button>
        <Button
          icon
          size="sm"
          variant="ghost"
          label="Previous page"
          disabled={page === 0}
          onClick={() => apiRef.current.setPage(page - 1)}
        >
          <Icon name="go-back" />
        </Button>
        <Button
          icon
          size="sm"
          variant="ghost"
          label="Next page"
          disabled={!hasNextPage}
          onClick={() => apiRef.current.setPage(page + 1)}
        >
          <Icon name="go-forward" />
        </Button>
        {!isUnknownRowCount ? (
          <Button
            icon
            size="sm"
            variant="ghost"
            label="Last page"
          disabled={pageCount === 0 || page >= pageCount - 1}
          onClick={() => apiRef.current.setPage(pageCount - 1)}
        >
            <Icon name="go-last" />
          </Button>
        ) : null}
      </div>
    </GridFooterContainer>
  );
};

const DataTable = withRefProp((props: DataTableProps) => {
  const {
    baseStyles,
    className,
    style,
    ref,
    autosizeKey,
    handleRef,
    columns,
    getDetailPanelContent,
    rowSelection = false,
    disableRowSelectionOnClick = true,
    selectedRowId,
    getRowClassName,
    classes,
    slots,
    pagination,
    rowCount,
    pageSizeOptions = [10, 25, 50, 100],
    ...otherProps
  } = props;
  const effectiveRowCount = rowCount ?? (otherProps.paginationMode === "server" ? -1 : undefined);
  const rowCountKnownForFooter = otherProps.paginationMode !== "server" || (rowCount !== undefined && rowCount >= 0);
  const apiRef = useGridApiRef() as React.MutableRefObject<GridApiPro>;
  React.useImperativeHandle(
    handleRef,
    () => ({
      scrollToIndexes: (params) => apiRef.current.scrollToIndexes(params),
    }),
    [apiRef, handleRef],
  );

  const normalizedColumns = React.useMemo(
    () => (columns as DataTableColumn[] | undefined)?.map(({ autoSize: _autoSize, ...column }) => column) ?? [],
    [columns],
  );

  const mergedColumns = React.useMemo(() => {
    if (!getDetailPanelContent) return normalizedColumns;
    if (normalizedColumns.some((column) => column.field === GRID_DETAIL_PANEL_TOGGLE_FIELD)) {
      return normalizedColumns;
    }

    const detailToggleColumn: GridColDef = {
      field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
      headerName: "",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      width: 56,
      align: "center",
      headerAlign: "center",
      resizable: false,
      renderCell: (params: GridRenderCellParams<any, boolean>) => {
        const expanded = Boolean(params.value);
        return (
          <Button icon
            size="sm"
            label={expanded ? "Collapse details" : "Expand details"}
            onClick={(event) => {
              event.stopPropagation();
              // @ts-ignore
              params.api.toggleDetailPanel(params.id);
            }}
          >
            <Icon name={expanded ? "collapse" : "expand"} />
          </Button>
        );
      },
    };

    return [detailToggleColumn, ...normalizedColumns];
  }, [normalizedColumns, getDetailPanelContent]);

  const autoSizeColumns = React.useMemo<string[]>(
    () => ((columns as DataTableColumn[] | undefined) ?? []).filter((column) => column.autoSize).map((column) => String(column.field)),
    [columns],
  );

  const computedAutosizeOptions = React.useMemo<GridAutosizeOptions | undefined>(() => {
    if (autoSizeColumns.length === 0) return undefined;
    return {
      columns: autoSizeColumns,
      includeHeaders: true,
      includeOutliers: true,
    };
  }, [autoSizeColumns]);

  React.useEffect(() => {
    if (!computedAutosizeOptions || autosizeKey === undefined) return;

    const id = requestAnimationFrame(() => {
      apiRef.current?.autosizeColumns?.(computedAutosizeOptions);
    });

    return () => cancelAnimationFrame(id);
  }, [apiRef, autosizeKey, computedAutosizeOptions]);

  const getSelectableRowClassName = React.useCallback<NonNullable<DataGridProProps["getRowClassName"]>>(
    (params) => clsx(
      getRowClassName?.(params),
      selectedRowId !== undefined && selectedRowId !== null && params.id === selectedRowId && "Mui-selected",
    ),
    [getRowClassName, selectedRowId],
  );

  return (
    <DataGridPro
      sx={{
        border: "none"
      }}
      classes={{
        ...(classes ?? {}),
        cell: clsx(classes?.cell, styles.cell),
      }}
      rowSelection={rowSelection}
      disableRowSelectionOnClick={disableRowSelectionOnClick}
      pagination={pagination}
      rowCount={effectiveRowCount}
      pageSizeOptions={pageSizeOptions}
      ref={ref}
      apiRef={apiRef}
      autosizeOnMount={autoSizeColumns.length > 0}
      autosizeOptions={computedAutosizeOptions}
      columns={mergedColumns}
      getDetailPanelContent={getDetailPanelContent}
      getRowClassName={getSelectableRowClassName}
      slots={{
        ...slots,
        baseMenuList: (slots?.baseMenuList ?? DataTableBaseMenuList) as any,
        baseMenuItem: slots?.baseMenuItem ?? DataTableBaseMenuItem,
        baseDivider: slots?.baseDivider ?? DataTableBaseDivider,
        baseIconButton: (slots?.baseIconButton ?? DataTableBaseIconButton) as any,
        baseButton: (slots?.baseButton ?? DataTableBaseButton) as any,
        baseCheckbox: (slots?.baseCheckbox ?? DataTableBaseCheckbox) as any,
        baseSelect: (slots?.baseSelect ?? DataTableBaseSelect) as any,
        baseSelectOption: slots?.baseSelectOption ?? DataTableBaseSelectOption,
        baseTextField: (slots?.baseTextField ?? DataTableBaseTextField) as any,
        columnMenuIcon: slots?.columnMenuIcon ?? DataTableColumnMenuIcon,
        columnMenuSortAscendingIcon: slots?.columnMenuSortAscendingIcon ?? DataTableSortAscendingIcon,
        columnMenuSortDescendingIcon: slots?.columnMenuSortDescendingIcon ?? DataTableSortDescendingIcon,
        columnMenuFilterIcon: slots?.columnMenuFilterIcon ?? DataTableFilterIcon,
        columnMenuHideIcon: slots?.columnMenuHideIcon ?? DataTableHideColumnIcon,
        columnMenuManageColumnsIcon: slots?.columnMenuManageColumnsIcon ?? DataTableManageColumnsIcon,
        footer: pagination
          ? slots?.footer ?? (() => <DataTableFooter rowCountKnown={rowCountKnownForFooter} />)
          : slots?.footer,
      }}
      data-deci-data-table="true"
      data-deci-base-styles={baseStyles !== false ? "true" : undefined}
      className={clsx(styles.root, className)}
      style={style}
      {...otherProps}
    />
  );
});

export default DataTable;
